import * as React from "react";

import { cn } from "@/shared/utils/utils";
import type { DataTableSkeletonProps } from "@/shared/type/skeleton.type";
import { Skeleton } from "./skeleton";

export const DataTableSkeleton = ({
  className,
  rowCount = 5,
  columnCount = 5,
  columns,
  showToolbar = true,
  showPagination = true,
  ...props
}: DataTableSkeletonProps): React.ReactElement => {
  const safeRowCount = Math.max(1, rowCount);
  const safeColumnCount = Math.max(1, columnCount);
  const rowIndexes = [...Array(safeRowCount).keys()];
  const columnIndexes = [...Array(safeColumnCount).keys()];

  return (
    <div
      aria-busy="true"
      aria-label="Loading table"
      data-slot="data-table-skeleton"
      className={cn("table-wrapper overflow-hidden", className)}
      {...props}
    >
      <span className="sr-only" role="status">
        Loading table data
      </span>

      {showToolbar && (
        <div
          aria-label="Loading table controls"
          data-slot="data-table-skeleton-toolbar"
          className="flex min-h-15 items-center justify-between gap-4 border-b border-border p-3.5"
        >
          <Skeleton className="h-4 w-40 max-w-[45%]" />
          <div className="flex gap-3">
            <Skeleton className="h-9 w-24" />
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="ai-custom-table w-full table-fixed border-collapse">
          <thead className="bg-border [&>tr]:h-11.5">
            <tr>
              {columnIndexes.map((columnIndex) => (
                <th key={columnIndex} className="px-3 py-2">
                  <Skeleton
                    className={cn(
                      "h-4 w-24 max-w-full",
                      columns?.[columnIndex]?.headerClassName,
                    )}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowIndexes.map((rowIndex) => (
              <tr
                key={rowIndex}
                data-slot="data-table-skeleton-row"
                className="h-11 border-b border-border"
              >
                {columnIndexes.map((columnIndex) => (
                  <td key={columnIndex} className="px-3 py-2">
                    <Skeleton
                      className={cn(
                        "h-4 w-full max-w-36",
                        columnIndex === 0 && "max-w-48",
                        columns?.[columnIndex]?.cellClassName,
                      )}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <div
          aria-label="Loading table pagination"
          data-slot="data-table-skeleton-pagination"
          className="flex min-h-15 flex-wrap items-center justify-between gap-4 border-t border-border p-3.5"
        >
          <Skeleton className="h-8.5 w-32" />
          <div className="flex items-center gap-3">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="size-8.5 rounded-full" />
            <Skeleton className="size-8.5 rounded-full" />
          </div>
        </div>
      )}
    </div>
  );
};
